import { useState } from "react";
import Backdrop from "@mui/material/Backdrop";
import Modal from "@mui/material/Modal";
import { showSnackBar } from "../../../../../store/common/snackBarSlice";
import CircularLoader from "../../../../../Components/CircularLoader/CircularLoader";
import { useDispatch } from "react-redux";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { loginUserByAdmin } from "../../../../../services/AdminServices/Users/users.services";
import { addUser } from "../../../../../store/userSlice";
import { Trans, useTranslation } from "react-i18next";

const LockIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 11c-1.104 0-2-.896-2-2V6c0-1.104.896-2 2-2s2 .896 2 2v3c0 1.104-.896 2-2 2zm3 0h1c1.104 0 2 .896 2 2v7c0 1.104-.896 2-2 2H8c-1.104 0-2-.896-2-2v-7c0-1.104.896-2 2-2h1"
    />
  </svg>
);

export default function LoginUserModel({ open, setOpen }) {
  const { t } = useTranslation("admin");
  const [adminPassword, setAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const handleClose = () => {
    setOpen(false);
    setAdminPassword("");
  };

  const handleLoginUser = async () => {
    try {
      setLoading(true);
      const res = await loginUserByAdmin(
        {
          password: adminPassword,
        },
        open?.id
      );

      if (res.error) {
        dispatch(
          showSnackBar({
            text: res?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
            severity: "error",
          })
        );
        setLoading(false);
        return;
      }

      // Clear localStorage and Redux store
      localStorage.clear();
      dispatch(addUser(null)); // Clear user in Redux (assumes null resets the store)

      const user = {
        ...res?.data?.user,
        token: res?.data?.token,
      };

      // Set updated localStorage
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("Token", res?.data?.token);
      localStorage.setItem("Id", res?.data?.user?.id);
      localStorage.setItem("Name", res?.data?.user?.username);
      localStorage.setItem("Email", res?.data?.user?.email);
      localStorage.setItem("Role", res?.data?.user?.role);
      localStorage.setItem("firstname", res?.data?.user?.firstname);
      localStorage.setItem("lastname", res?.data?.user?.lastname);
      localStorage.setItem("profile_image", res?.data?.user?.profile_image);
      localStorage.setItem("Balance", res?.data?.user?.balance);

      if (
        res?.data?.user?.role === "Shipper" ||
        res?.data?.user?.role === "ShipperUser"
      ) {
        localStorage.setItem("Carriers", JSON.stringify(res?.data?.carrier));
      }

      dispatch(addUser(user));

      dispatch(
        showSnackBar({
          text: res?.data?.message,
          severity: "success",
        })
      );

      // Refresh the page
      window.location.reload();
    } catch (error) {
      dispatch(
        showSnackBar({
          text: error?.message || t("users.somethingWentWrong"),
          severity: "error",
        })
      );
      setLoading(false);
    }
  };

  const canSubmit = adminPassword.trim().length > 0 && !loading;

  return (
    <Modal
      open={open}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
    >
      <div className="absolute top-1/2 left-1/2 w-full max-w-md transform -translate-x-1/2 -translate-y-1/2 bg-ui-surface p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-center mb-6">
          {t("users.loginTo", { name: open?.username ?? "" })}
        </h2>

        <p className="text-sm text-ui-text-muted text-center mb-4">
          <Trans
            t={t}
            i18nKey="users.switchHint"
            values={{ name: open?.username ?? "", role: open?.role ?? "" }}
            components={{ b: <b /> }}
          />
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-ui-text mb-2">
            {t("users.adminPassword")} <span className="text-state-danger">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ui-text-muted">
              <LockIcon />
            </span>
            <input
              type="text"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder={t("users.enterYourPassword")}
              autoComplete="new-admin-password"
              name={`admin-password-${Date.now()}`}
              style={{
                WebkitTextSecurity: showPassword ? "none" : "disc",
              }}
              className="w-full h-12 pl-10 pr-10 border rounded-lg bg-ui-surface text-ui-text placeholder:text-ui-text-muted focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ui-text-muted hover:text-ui-text"
            >
              {showPassword ? (
                <AiOutlineEyeInvisible size={20} />
              ) : (
                <AiOutlineEye size={20} />
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 rounded-md border border-ui-border text-ui-text hover:bg-ui-surface-sunken transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common:actions.cancel")}
          </button>
          <button
            onClick={handleLoginUser}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <CircularLoader /> : t("common:actions.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
